<script setup lang="ts">
import PerformanceBase from '@/components/PerformanceBase.vue'
import { onMounted, ref } from 'vue'
import { Autoplay } from "swiper/modules"
import Swiper from "swiper"
import { performanceHistory } from '@/lib/external-data/performanceHistory'
import PrimaryButton from '@/components/utils/PrimaryButton/PrimaryButton.vue'
import { useLocalStorage } from '@vueuse/core'
import formatRoi from '@/lib/formatRoi'
import useAosRefresh from '@/composables/useAosRefresh'

const selectedPerformanceDisplay = useLocalStorage<'%' | 'x'>('selected-performance-display', 'x', {initOnMounted: true})

const swiperElRef = ref<HTMLDivElement>();
import {useI18n} from "vue-i18n";
const {locale} = useI18n({
  useScope: 'global'
});

onMounted(() => {
  const swiperEl = swiperElRef.value;
  if (!swiperEl) return;

  new Swiper(swiperEl, {
    modules: [Autoplay],
    slidesPerView: "auto",
    simulateTouch: true,
    speed: 10000,
    autoplay: {
      delay: 0,
      disableOnInteraction: false,
    },
  })
})

useAosRefresh()

</script>

<template>

<PerformanceBase>
  <template #header >

    {{ $t('performance_history') }}

  </template>
  <template #default="{ selectedAudienceSize }" >
    <div class="swiper w-screen" ref="swiperElRef">
      <div class="swiper-wrapper w-max mt-7.5 space-x-5 flex ease-linear">
        <div v-for="(performance, index) in performanceHistory[selectedAudienceSize]" :key="index"
          class="swiper-slide min-w-61.5 py-4 px-4.5 border-2 border-brand-stroke/10 rounded-3">
          <div class="flex items-end">
            <span class="font-bold leading-none">{{ performance.month }}</span>
            <span class="text-xs font-semibold opacity-40 ml-1.5 leading-none">
              {{ performance.year }}
            </span>
          </div>
          <div class="flex items-end my-4">
            <span class="font-bold text-3xl text-brand-gradient leading-none">
              {{ formatRoi(performance.pnl, selectedPerformanceDisplay) }}
            </span>
            <span class="font-bold text-sm opacity-40 ml-1.5 leading-none">
              <template v-if="selectedPerformanceDisplay === '%'" >
                ROI
              </template>
              <template v-if="selectedPerformanceDisplay === 'x'" >
                {{ $t('on_capital')}}
              </template>  
            </span>
          </div>
          <span class="inline-block">
            {{ performance.winRate }}
            <span class="opacity-40">win rate</span></span>
          <div>
            {{ performance.avgTrade }}
            <span class="opacity-40"> {{$t('average_trade')}} ROI</span>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-6 flex gap-x-1.5 gap-y-5 justify-center items-center sm:flex-row flex-col container">
      <PrimaryButton 
        size="sm" 
        icon="i-material-symbols:keyboard-arrow-right"
        :href="`/${locale.slice(0,2)}/crypto-trading-bot-earnings-calculator`"
      >
        {{ $t('calculate_your_earnings_and_start_free_trial') }}
      </PrimaryButton>
    </div>

  </template>

</PerformanceBase>

</template>